// Delt HTTP-klient for intern-API-et. Samler base-URL, Bearer-token, x-correlation-id/ETag-fangst
// og feilnormalisering ett sted, så action-thunkene slipper å reimplementere dette hver for seg.
//
// Returnerer et normalisert resultat:
//   { ok, status, data, problemDetails, xCorrelationId, etag, response, aborted, networkError }
// - aborted: true  -> forespørselen ble bevisst avbrutt (AbortController) - IKKE en feil.
// - networkError: true (status 0) -> nettverksfeil (offline o.l.).
//
// Ved 401 forsøkes ÉN stille token-fornyelse (signinSilent) + nytt forsøk med ferskt token, slik at
// en utløpt token midt i en økt ikke feiler forespørselen.

import { getEnvironmentVariable } from "helpers/environmentVariableHelpers.js";

// Deles på tvers av samtidige 401-er så vi bare fornyer én gang.
let silentRenewPromise = null;

const silentlyRenewAccessToken = async () => {
    try {
        if (!silentRenewPromise) {
            // Lazy import: unngå å dra inn OIDC-UserManager ved modul-last (og i jest) med mindre en
            // 401-fornyelse faktisk trengs.
            silentRenewPromise = import("utils/userManager")
                .then((module) => module.default)
                .then((userManager) => userManager.signinSilent())
                .finally(() => {
                    silentRenewPromise = null;
                });
        }
        const user = await silentRenewPromise;
        return user?.access_token || null;
    } catch {
        return null;
    }
};

const performRequest = async (path, options) => {
    const {
        method = "GET",
        accessToken,
        body,
        headers = {},
        signal,
        parse = "json", // "json" | "blob" | "none"
        baseUrl, // overstyr base-URL (f.eks. eksternt API); default = internalApiUrl
        parseErrorBody = true // forsøk å lese ProblemDetails-body ved !ok
    } = options;

    const base = baseUrl != null ? baseUrl : getEnvironmentVariable("internalApiUrl");
    const url = `${base}${path}`;

    const finalHeaders = { ...headers };
    if (accessToken) {
        finalHeaders.Authorization = `Bearer ${accessToken}`;
    }

    // Auto-serialiser JSON-body; la string og FormData gå urørt (FormData setter egen Content-Type).
    let finalBody = body;
    const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
    if (body != null && !isFormData && typeof body !== "string") {
        finalBody = JSON.stringify(body);
        if (!finalHeaders["Content-Type"]) {
            finalHeaders["Content-Type"] = "application/json";
        }
    }

    let response;
    try {
        response = await fetch(url, { method, headers: finalHeaders, body: finalBody, signal });
    } catch (error) {
        if (error?.name === "AbortError") {
            return { aborted: true };
        }
        return { ok: false, status: 0, networkError: true };
    }

    const xCorrelationId = response.headers?.get?.("x-correlation-id");
    const etag = response.headers?.get?.("etag");

    if (!response.ok) {
        let problemDetails;
        if (parseErrorBody) {
            try {
                problemDetails = await response.json();
            } catch {
                // ingen/ugyldig feilbody
            }
        }
        return { ok: false, status: response.status, xCorrelationId, etag, problemDetails, response };
    }

    let data;
    if (parse === "json") {
        try {
            data = await response.json();
        } catch {
            data = null;
        }
    } else if (parse === "blob") {
        data = await response.blob();
    }

    return { ok: true, status: response.status, data, xCorrelationId, etag, response };
};

export const apiRequest = async (path, options = {}) => {
    const result = await performRequest(path, options);

    // Ett forsøk på stille token-fornyelse ved 401, deretter nytt forsøk med ferskt token. Krever at
    // forespørselen faktisk var autentisert (accessToken satt) og at vi ikke allerede har prøvd.
    if (result.status === 401 && options.accessToken && !options.retriedAfterRenew) {
        const refreshedToken = await silentlyRenewAccessToken();
        if (refreshedToken) {
            return performRequest(path, { ...options, accessToken: refreshedToken, retriedAfterRenew: true });
        }
    }

    return result;
};
